import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { Form, Input, Radio } from 'antd';
import toast from 'react-hot-toast';
import PaymentMethodInfo from './component/PaymentMethodInfo';
import BankTransfer from './component/BankTransfer';
import UPI from './component/UPI';
import LoadableButton from '../../../../components/buttons/LoadableButton';
import { GetCredentialsApi, GetWalletApi } from '../../../../api/request/wallet/withdraw';
import { GetPaymentGatewayApi } from '../../../../api/request/wallet/deposit';
import Paypal from './component/Paypal';
import Crypto from './component/Crypto';
import RMDT_Token from './component/RMDT_Token';
import UsdtWithdraw from './component/UsdtWithdraw';
import UPSWithdraw from './component/UPSWithdraw';
import WithdrawRequest from './WithdrawRequest';
import CashWithdraw from './component/CashWithdraw';
import { GetCurrentCurrencyRateApi } from '../../../../api/request/wallet/cashdeposit';

const Withdraw2 = () => {
    const [form] = Form.useForm();
    const { userDetails } = useSelector(state => state?.user);
    const [walletDetails, setWalletDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [amount, setAmount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [step, setStep] = useState(1);
    const [isRefresh, setIsRefresh] = useState(false);
    const [credentials, setCredentials] = useState([]);
    const [currencyRate, setCurrencyRate] = useState(null);
    const [paymentGateways, setPaymentGateways] = useState([]);

    const getWalletDetails = async () => {
        setIsLoading(true);
        try {
            const params = { RuleType: 'Withdraw' }
            const { data } = await GetWalletApi(userDetails?._id, new URLSearchParams(params).toString());
            if (data?.success) setWalletDetails(data?.data)
        } catch (error) {
            setIsLoading(false);
        }
    }

    const getPaymentGateways = async () => {
        try {
            const { data } = await GetPaymentGatewayApi(2);
            if (data?.success) setPaymentGateways(data?.data || []);
        } catch (error) {
            if (error?.response?.data?.error?.message)
                toast.error(error?.response?.data?.error?.message);
        }
    }

    const getCredentials = async () => {
        try {
            const { data } = await GetCredentialsApi();
            if (data?.success) {
                setCredentials(data?.data);
            }
        } catch (error) { }
    }
    const getCurrentCurrencyRate = async () => {
        try {
            const { data } = await GetCurrentCurrencyRateApi();
            if (data?.success) setCurrencyRate(data?.data);
        } catch (error) {
        }
    }

    useEffect(() => {
        getWalletDetails();
        getCredentials();
        getCurrentCurrencyRate();
        getPaymentGateways();
    }, []);

    const validateAmount = (_, value) => {
        if (!value) {
            setStep(1);
            return Promise.reject(new Error('Please enter amount.'));
        }

        if (Number(value) <= 0) {
            setStep(1);
            return Promise.reject(new Error('Please enter a valid amount.'));
        }

        if (walletDetails?.isRuleActive) {
            if (amount < walletDetails?.minAmount) {
                setStep(1);
                return Promise.reject(new Error(`Amount must be at least $${walletDetails?.minAmount}.`));
            }
            if (amount > walletDetails?.maxAmount) {
                setStep(1);
                return Promise.reject(new Error(`Amount cannot exceed $${walletDetails?.maxAmount}.`));
            }
        }

        return Promise.resolve();
    };

    const handleSubmit = (values) => {
        if (values?.amount && values?.paymentMethod) setStep(2);
    }

    const resetAll = () => {
        form.resetFields();
        setAmount(0);
        setPaymentMethod(null);
        setStep(1);
        setIsRefresh(true);
    }

    const renderStep = () => {
        switch (paymentMethod?.name) {
            case "BankTransfer":
            case "Bank Transfer":
                return <BankTransfer amount={amount} resetAll={resetAll} details={credentials?.bankDetails} />;
            case "UPI":
                return <UPI amount={amount} resetAll={resetAll} />;
            case "Paypal":
                return <Paypal amount={amount} resetAll={resetAll} />;
            case "Crypto":
                return <Crypto amount={amount} resetAll={resetAll} />;
            case "RMDT":
            case "RMDT Token":
                return <RMDT_Token amount={amount} resetAll={resetAll} />;
            case "USDT_TRC20":
            case "USDT-TRC20":
                return <UsdtWithdraw amount={amount} resetAll={resetAll} withdrawalMethod={paymentMethod?.withdrawMethod || 8} addressLabel="USDT TRC20 Wallet Address" />;
            case "USDT BEP20":
            case "USDT_BEP20":
                return <UsdtWithdraw amount={amount} resetAll={resetAll} withdrawalMethod={paymentMethod?.withdrawMethod || 9} addressLabel="USDT BEP20 Wallet Address" />;
            case "UPS":
                return <UPSWithdraw amount={amount} resetAll={resetAll} />;
            case "Cash":
                return <CashWithdraw amount={amount} resetAll={resetAll} details={credentials?.mobileNumbers} />;
            default:
                return null;
        }
    }

    const isRmdtMethod = paymentMethod?.name === "RMDT" || paymentMethod?.name === "RMDT Token";
    const isUsdtMethod = paymentMethod?.name === "USDT TRC20" || paymentMethod?.name === "USDT-TRC20" || paymentMethod?.name === "USDT BEP20" || paymentMethod?.name === "USDT-BEP20";
    
    return (
        <div className='space-y-5'>
            <PaymentMethodInfo walletDetails={walletDetails} paymentMethod={paymentMethod} type="withdraw"/>

            <div className='grid grid-cols-8 gap-5'>
                <div className='col-span-8 xl:col-span-3 border-2 border-light rounded-lg p-5'>
                    <Form form={form} layout='vertical' className='space-y-5' onFinish={handleSubmit}>
                        <Form.Item
                            name='amount'
                            label={
                                isRmdtMethod
                                    ? <span className="text-lg text-gray-500 font-medium">Amount In USD <span className='text-lg primary font-semibold'>($1 = {paymentMethod?.currencyRate} RMDT)</span></span>
                                    : isUsdtMethod
                                        ? <span className="text-lg text-gray-500 font-medium">Amount In USD</span>
                                        : <span className="text-lg text-gray-500 font-medium">Amount In USD<span className='text-lg primary font-semibold'> (${currencyRate?.baseCurrencyRate} = {currencyRate?.targetCurrencyWithdrawRate} {currencyRate?.targetCurrency})</span></span>
                            }
                            rules={[{ validator: validateAmount }]}
                        >
                            <Input
                                type='number'
                                placeholder="Amount in USD"
                                size='large'
                                prefix="$"
                                addonAfter={
                                    isRmdtMethod
                                        ?
                                        <span className="text-xs">RMDT {Number(amount || 0) * (paymentMethod?.currencyRate || 0)}</span>
                                        : null
                                }
                                onChange={(e) => setAmount(e?.target?.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            name="paymentMethod"
                            label={<span className="text-lg text-gray-500 font-medium">Select Payment Method</span>}
                            rules={[{ required: true, message: 'Please select a payment method.' }]}
                            required={false}
                        >
                            <Radio.Group className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
                                {paymentGateways?.map((item) => (
                                    <Radio
                                        key={item?.id}
                                        value={item?.id}
                                        className={`flex items-center gap-1 border-2 rounded-lg p-3 m-0 ${paymentMethod?.id === item?.id ? 'border-primary' : 'border-light'}`}
                                        onChange={() => setPaymentMethod(item)}
                                    >
                                        <div className='flex items-center gap-3'>
                                            {item?.paymentLogoUrl && (
                                                <img
                                                    src={item?.paymentLogoUrl}
                                                    alt="Icon"
                                                    className="h-[35px] w-[35px] object-contain"
                                                />
                                            )}
                                            <span className='break-all'>{item?.name}</span>
                                        </div>
                                    </Radio>
                                ))}
                            </Radio.Group>
                        </Form.Item>

                        <div className='flex items-center justify-end'>
                            <LoadableButton
                                className='bg-primary text-sm text-white font-semibold rounded-lg px-4 py-2 uppercase themeHover duration-500'
                                type="submit"
                                lable='Next'
                            />
                        </div>
                    </Form>
                </div>

                <div className='col-span-8 xl:col-span-5 border-2 border-light rounded-lg p-5'>
                    {step === 2 ? (
                        <>
                            {renderStep()}
                        </>
                    ) : (
                        <div className='h-full flex items-center justify-center'>
                            <p className='text-lg text-gray-500'>Please select a payment method.</p>
                        </div>
                    )}
                </div>
            </div>

            <WithdrawRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
        </div>
    )
}

export default Withdraw2